import { createTRPCReact, httpLink } from '@trpc/react-query';
import type { AppRouter } from '@lecturo/server/trpc/appRouter';

// Re-export AppRouter type for convenience
export type { AppRouter };

/**
 * tRPC React hooks
 * Use these hooks in components for type-safe API calls
 *
 * @example
 * ```typescript
 * // List tenants with infinite scroll
 * const { data, fetchNextPage } = trpc.tenant.list.useInfiniteQuery(
 *   { limit: 20 },
 *   { getNextPageParam: (lastPage) => lastPage.cursor }
 * );
 *
 * // Create tenant
 * const createTenant = trpc.tenant.create.useMutation();
 * createTenant.mutate({ name: 'New University' });
 * ```
 */
export const trpc = createTRPCReact<AppRouter>();

/**
 * Get authentication token from auth store
 * Used by tRPC client to add Authorization header
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const { authService } = await import('../../features/authentication/store/authStore');
    const tokens = authService?.getCurrentTokens();
    return tokens?.idToken || null;
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return null;
  }
}

/**
 * Create tRPC client with custom routing for microservice architecture
 * Each feature router gets its own Lambda endpoint
 */
export function createTRPCClient() {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

  return trpc.createClient({
    links: [
      // Use httpLink for individual requests with dynamic URL
      httpLink({
        url: `${apiBaseUrl}/trpc/tenant`,
        // Add authentication header
        headers: async () => {
          const token = await getAuthToken();
          return {
            authorization: token ? `Bearer ${token}` : '',
          };
        },
        // Use fetch transformer to route to correct endpoint and strip feature prefix
        fetch: async (url, options) => {
          // Use headers as provided (already includes auth header from headers function)
          const headers = new Headers(options?.headers);

          // Parse the URL to extract and modify the path
          const urlObj = new URL(url.toString());
          const pathParts = urlObj.pathname.split('/').filter(Boolean);

          // Path format: /trpc/tenant/tenant.list -> we need /trpc/tenant/list
          // The last part is the procedure path which includes feature prefix
          if (pathParts.length >= 3) {
            const procedureFullPath = pathParts[pathParts.length - 1]; // e.g., "tenant.list"
            const [featureName, ...procedureParts] = procedureFullPath.split('.');

            if (procedureParts.length > 0) {
              // Strip the feature prefix from procedure path
              const procedurePath = procedureParts.join('.');
              pathParts[pathParts.length - 1] = procedurePath;

              // Update URL to correct feature endpoint
              pathParts[pathParts.length - 2] = featureName; // ensure correct feature in path
              urlObj.pathname = '/' + pathParts.join('/');
            }
          }

          return fetch(urlObj.toString(), { ...options, headers });
        },
      }),
    ],
  });
}
